import { ChevronLeft, ChevronRight, Info, Search, Sprout } from "lucide-react";
import { inject, observer } from "mobx-react";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FormattedNumber } from "react-intl";
import { Link } from "react-router-dom";
import {
  Badge,
  Button,
  Card,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHead,
  TableRow,
} from "randmarcomps";
import { useManufacturer } from "../../contexts/WithManufacturer";
import Path from "../../routes/Path";
import { OrderDetail } from "../../stores/document/Document";
import { RootStore } from "../../stores/rootStore";
import ProductDetailsModal from "../products/ProductDetailsModal";
import CurrencyUtils from "../util/CurrencyUtils";
import FormatDateFromInt, { getCurrentDate } from "../util/DateTimeUtil";

interface IProps {
  store?: RootStore;
  productOrders?: OrderDetail[];
  defaultRowsPerPage?: number;
  withSort?: boolean;
  withHeader?: boolean;
  variant?: "default" | "timeline";
  withStatus?: boolean;
  withSearch?: boolean;
}

const rowsPerPageOptions = [10, 20, 50, 100, 250, 500] as const;

const OpenOrdersGrid: React.FC<IProps> = ({
  store,
  productOrders,
  defaultRowsPerPage,
  withSort,
  withHeader,
  variant = "default",
  withStatus = false,
  withSearch = true,
}) => {
  const { applicationId } = useManufacturer();
  const [searchValue, setSearchValue] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(defaultRowsPerPage || 500);
  const isTimelineVariant = variant === "timeline";

  const handlePreviousPage = useCallback(() => setPage((prev) => Math.max(prev - 1, 0)), []);
  const handleNextPage = useCallback((totalPages: number) => setPage((prev) => Math.min(prev + 1, totalPages - 1)), []);

  const ordersToDisplay = useMemo(() => {
    if (!productOrders) return [];
    if (withSort) {
      return [...productOrders]
        .sort((a, b) => (a.mpn || "").localeCompare(b.mpn || ""))
        .sort((b, a) => (a.warehouseCode || "").localeCompare(b.warehouseCode || ""))
        .sort((b, a) => (a.orderNumber || "").localeCompare(b.orderNumber || ""));
    }
    return productOrders;
  }, [productOrders, withSort]);

  const warehousesStore = store?.warehousesStore;

  const normalizedSearchValue = searchValue.trim().toLowerCase();
  const showSearch = withSearch && ordersToDisplay.length > 3;

  const filteredOrders = useMemo(() => {
    if (!normalizedSearchValue) return ordersToDisplay;
    return ordersToDisplay.filter((row) => {
      const haystack = [
        row.resellerName,
        row.resellerId,
        row.orderNumber,
        row.poNumber,
        row.mpn,
        row.randmarSKU,
        row.warehouseCode,
        row.documentType,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return haystack.includes(normalizedSearchValue);
    });
  }, [normalizedSearchValue, ordersToDisplay]);

  useEffect(() => {
    setPage(0);
  }, [normalizedSearchValue]);

  const totalRows = filteredOrders.length;
  const safeRowsPerPage = Math.max(rowsPerPage, 1);
  const totalPages = Math.max(Math.ceil(totalRows / safeRowsPerPage), 1);
  const safePage = Math.min(page, totalPages - 1);
  const startRow = safePage * safeRowsPerPage;
  const endRow = Math.min(startRow + safeRowsPerPage, totalRows);

  const getRowClassName = useCallback(
    (row: OrderDetail) => {
      const base = "h-14 transition-colors";
      const rowTone =
        row.warehouseCode === "3PLE"
          ? "bg-amber-50/50 hover:bg-amber-50"
          : row.documentType === "Shipment"
            ? "bg-emerald-50/40 hover:bg-emerald-50/70"
            : row.documentDate === getCurrentDate()
              ? "bg-sky-50/40 hover:bg-sky-50/70"
              : "hover:bg-slate-50";

      if (!isTimelineVariant) {
        return [base, rowTone].join(" ");
      }

      return [base, rowTone, "border-b border-slate-100"].join(" ");
    },
    [isTimelineVariant]
  );

  const getWarehouseNameFromCode = useCallback(
    (code?: string) => {
      if (!code || !warehousesStore) {
        return code || "—";
      }
      return warehousesStore.getWarehouseNameFromCode(code) || code;
    },
    [warehousesStore]
  );

  const getStatusBadge = useCallback((row: OrderDetail) => {
    if (row.documentType === "Shipment") {
      return { label: "Shipped", variant: "success" as const };
    }
    if (row.documentType === "Order" && row.warehouseCode === "3PLE") {
      return { label: "Delayed", variant: "warning" as const };
    }
    return { label: "Processing", variant: "secondary" as const };
  }, []);

  if (ordersToDisplay.length === 0) {
    return (
      <Card className="w-full rounded-2xl border border-dashed border-gray-100 bg-gray-50/60 p-8 shadow-sm">
        <div className="mx-auto flex max-w-lg flex-col items-center gap-3 text-center">
          <Info className="h-10 w-10 text-slate-300" aria-hidden="true" />
          <p className="text-xl font-medium text-slate-900">No orders to show</p>
          <p className="text-[15px] text-slate-500">Purchase orders and shipments will appear here as activity comes in.</p>
        </div>
      </Card>
    );
  }

  if (filteredOrders.length === 0) {
    return (
      <Card className="w-full rounded-2xl border border-dashed border-gray-100 bg-gray-50/60 p-8 shadow-sm">
        <div className="mx-auto flex max-w-lg flex-col items-center gap-3 text-center">
          <Info className="h-10 w-10 text-slate-300" aria-hidden="true" />
          <p className="text-xl font-medium text-slate-900">No matching orders</p>
          <p className="text-[15px] text-slate-500">Try a different search or clear the filter.</p>
          <Button type="button" variant="outline" className="rounded-xl" onClick={() => setSearchValue("")}>
            Clear search
          </Button>
        </div>
      </Card>
    );
  }

  return (
    <Card className="w-full overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm">
      {showSearch ? (
        <div className="border-b border-gray-100 px-4 py-3">
          <div className="relative max-w-sm">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" aria-hidden="true" />
            <Input
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              placeholder="Search orders…"
              className="h-11 rounded-lg pl-10"
              aria-label="Search orders"
            />
          </div>
        </div>
      ) : null}
      <div className="w-full overflow-x-auto">
        <Table>
          {withHeader ? (
            <TableHeader className="bg-slate-50">
              <TableRow className="h-12">
                <TableHead className="whitespace-nowrap text-[12px] font-semibold uppercase tracking-wide text-slate-500">
                  Reseller
                </TableHead>
                <TableHead className="whitespace-nowrap text-[12px] font-semibold uppercase tracking-wide text-slate-500">
                  Order
                </TableHead>
                <TableHead className="whitespace-nowrap text-[12px] font-semibold uppercase tracking-wide text-slate-500">
                  PO
                </TableHead>
                <TableHead className="whitespace-nowrap text-[12px] font-semibold uppercase tracking-wide text-slate-500">
                  MPN
                </TableHead>
                <TableHead className="whitespace-nowrap text-[12px] font-semibold uppercase tracking-wide text-slate-500">
                  Date
                </TableHead>
                <TableHead className="whitespace-nowrap text-[12px] font-semibold uppercase tracking-wide text-slate-500">
                  Location
                </TableHead>
                {withStatus ? (
                  <TableHead className="whitespace-nowrap text-[12px] font-semibold uppercase tracking-wide text-slate-500">
                    Status
                  </TableHead>
                ) : null}
                <TableHead className="whitespace-nowrap text-[12px] font-semibold uppercase tracking-wide text-slate-500">
                  Qty
                </TableHead>
                <TableHead className="whitespace-nowrap text-[12px] font-semibold uppercase tracking-wide text-slate-500">
                  Unit
                </TableHead>
                <TableHead className="w-12 text-right text-[12px] font-semibold uppercase tracking-wide text-slate-500" />
                <TableHead className="w-12 text-right text-[12px] font-semibold uppercase tracking-wide text-slate-500" />
              </TableRow>
            </TableHeader>
          ) : null}
          <TableBody>
            {filteredOrders.slice(startRow, endRow).map((row, index) => {
              const status = withStatus ? getStatusBadge(row) : null;

              return (
                <TableRow key={`${row.orderNumber ?? "order"}-${index}`} className={getRowClassName(row)}>
                  <TableCell className="whitespace-nowrap font-semibold text-slate-900">
                    <Link to={Path.reseller(applicationId, row.resellerId)} className="hover:underline">
                      {row.resellerName}
                    </Link>
                  </TableCell>
                  <TableCell className="whitespace-nowrap">
                    <Link to={Path.generalDocument(applicationId, row.orderNumber)} className="text-slate-900 hover:underline">
                      {row.orderNumber}
                    </Link>
                  </TableCell>
                  <TableCell className="whitespace-nowrap">
                    <Link to={Path.generalDocument(applicationId, row.orderNumber)} className="text-slate-900 hover:underline">
                      {row.poNumber}
                    </Link>
                  </TableCell>
                  <TableCell className="whitespace-nowrap font-semibold">
                    <Link to={Path.product(applicationId, row.randmarSKU)} className="text-slate-900 hover:underline">
                      {row.mpn}
                    </Link>
                  </TableCell>
                  <TableCell className="whitespace-nowrap text-slate-700">{FormatDateFromInt(row.orderDate)}</TableCell>
                  <TableCell className="whitespace-nowrap text-slate-700">{getWarehouseNameFromCode(row.warehouseCode)}</TableCell>
                  {withStatus ? (
                    <TableCell className="whitespace-nowrap">
                      <Badge variant={status?.variant ?? "secondary"} size="sm">
                        {status?.label ?? "—"}
                      </Badge>
                    </TableCell>
                  ) : null}
                  <TableCell className="whitespace-nowrap font-semibold text-slate-900">{row.quantity}</TableCell>
                  <TableCell className="whitespace-nowrap text-slate-900">
                    <FormattedNumber {...CurrencyUtils.format} value={row.unitPrice || 0} />
                  </TableCell>
                  <TableCell className="whitespace-nowrap text-right">
                    {row.opportunityManufacTurerId === applicationId ? (
                      <Link
                        title={`Opportunity ${row.opportunityManufacturerNumber}`}
                        to={Path.opportunity(applicationId, row.opportunityManufacturerNumber)}
                        className="inline-flex items-center justify-center rounded-xl p-2 text-[color:var(--ops-accent)] hover:bg-slate-50"
                      >
                        <Sprout className="h-4 w-4" aria-hidden="true" />
                      </Link>
                    ) : null}
                  </TableCell>
                  <TableCell className="whitespace-nowrap text-right">
                    <ProductDetailsModal randmarSKU={row.randmarSKU || ""} />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      {totalRows > safeRowsPerPage ? (
        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-gray-100 px-4 py-3">
          <p className="text-[13px] text-slate-500">
            Showing <span className="font-semibold text-slate-900">{startRow + 1}</span>–
            <span className="font-semibold text-slate-900">{endRow}</span> of{" "}
            <span className="font-semibold text-slate-900">{totalRows}</span>
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <Select
              value={String(safeRowsPerPage)}
              onValueChange={(value) => {
                setRowsPerPage(Number.parseInt(value, 10));
                setPage(0);
              }}
            >
              <SelectTrigger className="h-9 w-[130px] rounded-xl">
                <SelectValue placeholder="Rows" />
              </SelectTrigger>
              <SelectContent>
                {rowsPerPageOptions.map((option) => (
                  <SelectItem key={option} value={String(option)}>
                    {option} rows
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button type="button" variant="outline" size="sm" className="h-9 rounded-xl px-3" onClick={handlePreviousPage} disabled={safePage === 0}>
              <ChevronLeft className="h-4 w-4" aria-hidden="true" />
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-9 rounded-xl px-3"
              onClick={() => handleNextPage(totalPages)}
              disabled={safePage >= totalPages - 1}
            >
              <ChevronRight className="h-4 w-4" aria-hidden="true" />
            </Button>
          </div>
        </div>
      ) : null}
    </Card>
  );
};

export default inject("store")(observer(OpenOrdersGrid));
